import { onBeforeUnmount, onMounted, ref } from 'vue';

/**
 * @module screenWidth
 *
 * @description See `screen_width_test.scss` for element styling;
 */

/** Hierarchy of breakpoints */
const breakpoints = ['small', 'medium', 'large', 'x-large', 'xx-large'];

/** Create a div for testing media query breakpoints */
function createTestElement() {
  let element = document.createElement('div');
  element.classList.add('screen-width-test');
  document.body.appendChild(element);
  return element;
}

/** Checks the `content` style attribute of the testing element to register the active breakpoint */
export function getActiveBreakpoint() {
  let element = createTestElement();
  let bp = window.getComputedStyle(element).content.replace(/"/g, '');
  element.remove();
  return bp;
}

/**
 * Tests that the actual screen width is at least the width of the requested breakpoint.
 *
 * @param {string} breakpointName - name of the breakpoint to test
 *
 * @example
 * // at screen width large
 * screenAtLeast('medium')
 * // = false
 *
 * @example
 * screenAtLeast('large')
 * // = true
 *
 * @example
 * screenAtLeast('x-large')
 * // = true
 */
export function screenAtLeast(breakpointName) {
  const activeBreakpoint = getActiveBreakpoint();

  return (breakpointName === activeBreakpoint) ||
    (breakpoints.indexOf(breakpointName) < breakpoints.indexOf(activeBreakpoint));
}

let handler, breakpointWas;

(() => {
  breakpointWas = getActiveBreakpoint();

  handler = () => {
    let breakpointIs = getActiveBreakpoint();
    if (breakpointWas !== breakpointIs) {
      window.dispatchEvent(new CustomEvent('eaimms:breakpoint.change'), {
        detail: {
          breakpoint: breakpointIs,
          previousBreakpoint: breakpointWas
        }
      });
      breakpointWas = breakpointIs;
    }
  };

  window.addEventListener('resize', handler, { passive: true });
})();

export function useBreakpoint() {
  const breakpoint = ref(getActiveBreakpoint());
  let handler = () => {
    let breakpointIs = getActiveBreakpoint();
    if (breakpointWas !== breakpointIs) {
      breakpoint.value = breakpointIs;
    }
  };

  onMounted(() => {
    window.addEventListener('resize', handler, { passive: true });
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handler);
  });

  return breakpoint;
}
